/**
 * ABC, turnover and dead/slow stock views (batch 8 T14c, #3036). Each renders exactly what the server answered for the
 * variant that was asked: the units variants carry no money field, so there is nothing to hide; the financial variants
 * show the known value and say what is not in it.
 */
import type { ReactNode } from 'react';
import { Alert, Chip, Stack, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type {
  AbcClass,
  DeadSlowStatus,
  WireAbc,
  WireDeadSlow,
  WireTurnover,
  WireTurnoverCostGroup,
  WireTurnoverUnitsGroup,
} from '@/hooks/useInventoryReports';
import {
  ExactMoney,
  Figure,
  MetricValue,
  Money,
  Named,
  PartialWarning,
  ReconciledTotal,
  ReportTable,
  SourceNote,
  Stat,
  Unknown,
  WindowInfo,
  useReportDisplay,
} from './ReportParts';
import { formatInstant, formatPercent } from './reportFormat';
import { statusReasonKey } from './reportLabels';

const CLASSES: readonly AbcClass[] = ['A', 'B', 'C', 'N', 'U'];

const Percent = ({ value }: { value: number | null }) => {
  const { language } = useReportDisplay();
  return <>{value === null ? '' : formatPercent(value, language)}</>;
};

export function AbcView({ data }: { data: WireAbc }) {
  const { t } = useTranslation('inventoryReports');
  const { language } = useReportDisplay();
  const financial = data.financial;
  return (
    <Stack spacing={2}>
      <WindowInfo window={data.window} />
      <Typography variant="body2">
        {t('abc.thresholds', { a: formatPercent(data.thresholds.aPercent, language), b: formatPercent(data.thresholds.bPercent, language) })}
      </Typography>
      {!data.classification.available && (
        <Alert severity="warning">
          <Unknown reason={data.classification.reason} /> {t('abc.classificationUnavailable')}
        </Alert>
      )}
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        <Stat label={t('abc.rankedUnits')}>
          <Figure value={data.rankedTotalUnits} />
        </Stat>
        {data.financial && <ReconciledTotal label={t(data.basis === 'cost' ? 'abc.rankedCost' : 'abc.rankedRevenue')} total={data.rounding} />}
      </Stack>
      {data.financial && <SourceNote source={data.source} />}
      <Typography variant="caption" color="text.secondary">
        {t('abc.classNote')}
      </Typography>
      <ReportTable label={t('abc.classesLabel')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('abc.class')}</TableCell>
            <TableCell align="right">{t('abc.parts')}</TableCell>
            <TableCell align="right">{t('abc.units')}</TableCell>
            <TableCell align="right">{t('abc.share')}</TableCell>
            {financial && <TableCell align="right">{t('abc.value')}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {CLASSES.map((cls) => {
            const c = data.classes[cls];
            return (
              <TableRow key={cls}>
                <TableCell>{t(`abc.classes.${cls}`)}</TableCell>
                <TableCell align="right"><Figure value={c.parts} /></TableCell>
                <TableCell align="right"><Figure value={c.units} /></TableCell>
                <TableCell align="right"><Percent value={c.sharePercent} /></TableCell>
                {data.financial && (
                  <TableCell align="right">
                    {cls === 'U' ? <Unknown reason={data.basis === 'revenue' ? 'no_revenue_identity' : 'cost_unconfirmed'} /> : <Money cents4={(data.classes[cls] as { valueCents4: string }).valueCents4} />}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </ReportTable>
      <ReportTable label={t('abc.rowsLabel')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('abc.rank')}</TableCell>
            <TableCell>{t('abc.part')}</TableCell>
            <TableCell>{t('abc.category')}</TableCell>
            <TableCell>{t('abc.class')}</TableCell>
            <TableCell align="right">{t('abc.units')}</TableCell>
            <TableCell align="right">{t('abc.share')}</TableCell>
            <TableCell align="right">{t('abc.cumulative')}</TableCell>
            {financial && <TableCell align="right">{t('abc.value')}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.rows.map((row) => (
            <TableRow key={row.partId}>
              <TableCell>{row.rank ?? ''}</TableCell>
              <TableCell><Named kind="part" id={row.partId} sku={row.sku} name={row.name} /></TableCell>
              <TableCell>{row.categoryId === null ? '' : <Named kind="category" id={row.categoryId} />}</TableCell>
              <TableCell>{row.class === null ? <Unknown reason="no_history" /> : <Chip size="small" label={t(`abc.classes.${row.class}`)} />}</TableCell>
              <TableCell align="right"><Figure value={row.netUnits} /></TableCell>
              <TableCell align="right"><Percent value={row.sharePercent} /></TableCell>
              <TableCell align="right"><Percent value={row.cumulativePercent} /></TableCell>
              {data.financial && (
                <TableCell align="right">
                  {'valueCents4' in row && row.valueCents4 !== null ? (
                    <Money cents4={row.valueCents4} cents={row.valueCents} />
                  ) : 'unknownReason' in row && row.unknownReason !== null ? (
                    <Unknown reason={row.unknownReason} />
                  ) : (
                    ''
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </ReportTable>
    </Stack>
  );
}

const groupName = (groupBy: 'part' | 'category', key: string) => <Named kind={groupBy} id={key} />;

export function TurnoverView({ data }: { data: WireTurnover }) {
  const { t } = useTranslation('inventoryReports');
  const { language } = useReportDisplay();
  const financial = data.financial;
  const numerator = (g: WireTurnoverUnitsGroup | WireTurnoverCostGroup) =>
    'numeratorCents4' in g ? (
      <MetricValue metric={g.numeratorCents4} render={(v) => <ExactMoney cents4={v} />} />
    ) : (
      <MetricValue metric={g.numeratorUnits} render={(v) => <Figure value={v} />} />
    );
  const edge = (g: WireTurnoverUnitsGroup | WireTurnoverCostGroup) =>
    'edgeSumCents4' in g ? (
      <MetricValue metric={g.edgeSumCents4} render={(v) => <ExactMoney cents4={v} />} />
    ) : (
      <MetricValue metric={g.edgeSumUnits} render={(v) => <Figure value={v} />} />
    );
  const row = (g: WireTurnoverUnitsGroup | WireTurnoverCostGroup, name: ReactNode) => (
    <TableRow key={g.key}>
      <TableCell>{name}</TableCell>
      <TableCell align="right"><Figure value={g.parts} /></TableCell>
      <TableCell align="right">{g.noHistoryParts > 0 ? t('turnover.noHistoryParts', { count: g.noHistoryParts }) : <Figure value={0} />}</TableCell>
      <TableCell align="right">{g.unavailableParts > 0 ? t('turnover.unavailableParts', { count: g.unavailableParts }) : <Figure value={0} />}</TableCell>
      <TableCell align="right">{numerator(g)}</TableCell>
      <TableCell align="right">{edge(g)}</TableCell>
      <TableCell align="right"><MetricValue metric={g.turnover} render={(v) => <Figure value={v} />} /></TableCell>
      <TableCell align="right"><MetricValue metric={g.daysInInventory} render={(v) => <Figure value={v} />} /></TableCell>
    </TableRow>
  );
  return (
    <Stack spacing={2}>
      <WindowInfo window={data.window} />
      <Typography variant="body2" color="text.secondary">
        {t('turnover.edges', { start: formatInstant(data.edges.start, data.window.timeZone, language), end: formatInstant(data.edges.end, data.window.timeZone, language) })}
      </Typography>
      {financial && <SourceNote source={data.source} />}
      <Typography variant="caption" color="text.secondary">
        {t(financial ? 'turnover.noteCost' : 'turnover.noteUnits')}
      </Typography>
      <ReportTable label={t('turnover.label')}>
        <TableHead>
          <TableRow>
            <TableCell>{t(data.groupBy === 'part' ? 'turnover.part' : 'turnover.category')}</TableCell>
            <TableCell align="right">{t('turnover.parts')}</TableCell>
            <TableCell align="right">{t('turnover.noHistory')}</TableCell>
            <TableCell align="right">{t('turnover.unavailable')}</TableCell>
            <TableCell align="right">{t(financial ? 'turnover.numeratorCost' : 'turnover.numeratorUnits')}</TableCell>
            <TableCell align="right">{t(financial ? 'turnover.edgeCost' : 'turnover.edgeUnits')}</TableCell>
            <TableCell align="right">{t('turnover.turnover')}</TableCell>
            <TableCell align="right">{t('turnover.days')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {row(data.total, <strong>{t('turnover.total')}</strong>)}
          {data.groups.map((g) => row(g, groupName(data.groupBy, g.key)))}
        </TableBody>
      </ReportTable>
    </Stack>
  );
}

const STATUS_COLOR: Record<DeadSlowStatus, 'error' | 'warning' | 'success' | 'default'> = {
  dead: 'error',
  slow: 'warning',
  active: 'success',
  no_history: 'default',
};

export function DeadSlowView({ data }: { data: WireDeadSlow }) {
  const { t } = useTranslation('inventoryReports');
  const { language } = useReportDisplay();
  const zone = data.window.timeZone;
  const unconfirmedOnPage = data.financial ? data.rows.reduce((count, row) => count + (row.unconfirmedQuantity > 0 ? 1 : 0), 0) : 0;
  return (
    <Stack spacing={2}>
      <WindowInfo window={data.window} />
      <Typography variant="body2" color="text.secondary">
        {t('dead.params', { dead: data.params.deadDays, slow: data.params.slowCoverageDays, window: data.params.windowDays, now: formatInstant(data.params.now, zone, language) })}
      </Typography>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        {(['dead', 'slow', 'active', 'no_history'] as const).map((status) => (
          <Stat key={status} label={t(`dead.status.${status}`)}>
            <Figure value={data.summary.counts[status]} />
          </Stat>
        ))}
        <Stat label={t('dead.agedStockParts')}>
          <Figure value={data.summary.agedStockParts} />
        </Stat>
        {data.financial && <ReconciledTotal label={t('dead.valueAtRisk')} total={data.summary.valueAtRisk} />}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {t('dead.summaryNote')}
      </Typography>
      {data.financial && (
        <>
          <SourceNote source={data.source} />
          <PartialWarning>
            {t('dead.partialValue')} {unconfirmedOnPage > 0 && t('dead.partialOnPage', { count: unconfirmedOnPage })}
          </PartialWarning>
        </>
      )}
      <ReportTable label={t('dead.label')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('dead.part')}</TableCell>
            <TableCell>{t('dead.statusLabel')}</TableCell>
            <TableCell align="right">{t('dead.onHand')}</TableCell>
            <TableCell align="right">{t('dead.oldestAge')}</TableCell>
            <TableCell align="right">{t('dead.lastConsumption')}</TableCell>
            <TableCell align="right">{t('dead.windowUnits')}</TableCell>
            <TableCell align="right">{t('dead.coverage')}</TableCell>
            <TableCell align="right">{t('dead.unbacked')}</TableCell>
            {data.financial && <TableCell align="right">{t('dead.unconfirmed')}</TableCell>}
            {data.financial && <TableCell align="right">{t('dead.knownValue')}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.rows.map((row) => (
            <TableRow key={row.partId}>
              <TableCell><Named kind="part" id={row.partId} sku={row.sku} name={row.name} /></TableCell>
              <TableCell>
                <Chip size="small" color={STATUS_COLOR[row.status]} label={t(`dead.status.${row.status}`)} />
                {row.statusReason !== null && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {t(statusReasonKey(row.statusReason))}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right"><Figure value={row.onHand} /></TableCell>
              <TableCell align="right">
                <MetricValue
                  metric={row.oldestRemainingAgeDays}
                  render={(days) => (
                    <>
                      <Figure value={days} />
                      {row.ageIsLowerBound && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          {t('dead.ageLowerBound')}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </TableCell>
              <TableCell align="right">
                {row.lastConsumptionAt === null ? (
                  <Unknown reason="no_history" />
                ) : (
                  <>
                    {formatInstant(row.lastConsumptionAt, zone, language)}
                    <Typography variant="caption" color="text.secondary" component="div">
                      <MetricValue metric={row.consumptionRecencyDays} render={(days) => t('dead.daysAgo', { count: days })} />
                    </Typography>
                  </>
                )}
              </TableCell>
              <TableCell align="right"><Figure value={row.windowUnits} /></TableCell>
              <TableCell align="right"><MetricValue metric={row.coverageDays} render={(v) => <Figure value={v} />} /></TableCell>
              <TableCell align="right"><Figure value={row.unbackedQuantity} /></TableCell>
              {'valueAtRiskCents4' in row && (
                <>
                  <TableCell align="right"><Figure value={row.unconfirmedQuantity} /></TableCell>
                  <TableCell align="right"><Money cents4={row.valueAtRiskCents4} cents={row.valueAtRiskCents} /></TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </ReportTable>
    </Stack>
  );
}
